import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Array of keys to map the random number to your i18n JSON files
const numberKeys = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

interface MathCaptchaProps {
  onValidate: (isValid: boolean, data?: { captchaId: string, userAnswer: number }) => void;
}

const MathCaptcha: React.FC<MathCaptchaProps> = ({ onValidate }) => {
  const { t } = useTranslation();
  const [captchaText, setCaptchaText] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState<{ num1: number, num2: number, operator: string, captchaId: string } | null>(null);
  const [inputValue, setInputValue] = useState('');

  // Generates a random integer between min and max (inclusive)
  const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

  // Wrapped in useCallback so it can react to language changes safely
  const generateCaptcha = useCallback(async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/captcha`);
        const data = await response.json();
        
        const { num1, num2, operator, captchaId } = data;
        
        // FORMATTER: Returns a digit (e.g., "3") or translated word (e.g., t("numbers.three"))
        const formatNumber = (num: number) => {
          return Math.random() > 0.5 ? num.toString() : t(`numbers.${numberKeys[num]}`);
        };
        
        // Create the display string mixing words and numbers
        const text = `${formatNumber(num1)} ${operator} ${formatNumber(num2)}`;

        setCaptchaText(text);
        setCaptchaChallenge({ num1, num2, operator, captchaId });
        setInputValue('');
        onValidate(false); // Reset validation state on parent
    } catch (error) {
        console.error("Failed to fetch captcha", error);
    }
  }, [t, onValidate]); // `t` dependency ensures re-generation if language changes

  // Generate a CAPTCHA when the component mounts or when the language changes
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Handle user input and pass validation status back to parent
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    const userAnswer = parseInt(val, 10);
    
    // We consider it "valid" for the frontend if the user entered anything
    // The real validation happens on the backend using the captchaId
    onValidate(val.length > 0, captchaChallenge ? {
        captchaId: captchaChallenge.captchaId,
        userAnswer: userAnswer
    } : undefined);
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <label className="block mb-2 text-sm font-medium text-gray-700">
        {t("login.captchaLabel", { text: captchaText }).split("<strong>").map((part, index) => {
          if (part.includes("</strong>")) {
            const [bold, rest] = part.split("</strong>");
            return (
              <React.Fragment key={index}>
                <strong className="text-blue-600">{bold}</strong>{rest}
              </React.Fragment>
            );
          }
          return part;
        })}
      </label>
      <div className="flex gap-2">
        <input
          type="number"
          value={inputValue}
          onChange={handleChange}
          placeholder={t("login.captchaPlaceholder")}
          className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          required
        />
        <button
          type="button"
          onClick={generateCaptcha}
          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors flex items-center gap-1 text-sm font-medium"
        >
          🔄 {t("login.captchaRefresh")}
        </button>
      </div>
    </div>
  );
}

export default MathCaptcha;
